// src/features/unit/config/unitConverterConfig.ts
import { UnitConverterConfig, Unit } from '../types/unitConverterTypes';
import Big from 'big.js';

// Import all required icons
import StraightenIcon from '@mui/icons-material/Straighten';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShutterSpeedIcon from '@mui/icons-material/ShutterSpeed';
import SpeedIcon from '@mui/icons-material/Speed';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import WavesIcon from '@mui/icons-material/Waves';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PowerIcon from '@mui/icons-material/Power';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import AngleIcon from '@mui/icons-material/ThreeSixty';
import VibrationIcon from '@mui/icons-material/Vibration';

import BoltIcon from '@mui/icons-material/Bolt';
import FlashAutoIcon from '@mui/icons-material/FlashAuto';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import OhmIcon from '@mui/icons-material/FlashOff';
import ConductivityIcon from '@mui/icons-material/PowerInput';

import MagneticIcon from '@mui/icons-material/PowerInput';//TODO:'@mui/icons-material/MagneticField';
import DensityIcon from '@mui/icons-material/Compress';
import ConcentrationIcon from '@mui/icons-material/Opacity';
import RadiationIcon from '@mui/icons-material/PowerInput';//TODO:'@mui/icons-material/Radioactive';


// Helper to create simple factor-based conversion functions
const factor = (toBase: string): { toBase: (v: Big) => Big; fromBase: (v: Big) => Big } => ({
    toBase: (v: Big) => v.times(toBase),
    fromBase: (v: Big) => v.div(toBase),
});

// Helper for linear/affine transformations (like temperature)
const affine = (toBaseFactor: string, toBaseOffset: string): { toBase: (v: Big) => Big; fromBase: (v: Big) => Big } => ({
    toBase: (v: Big) => v.minus(toBaseOffset).times(toBaseFactor),
    fromBase: (v: Big) => v.div(toBaseFactor).plus(toBaseOffset),
});

// --- Base Unit Definitions ---
const defineUnit = (id: string, nameKey: string, symbolKey: string, descKey: string, conversion: { toBase: (v: Big) => Big; fromBase: (v: Big) => Big }): Unit => ({
    id, nameKey, symbolKey, descKey, ...conversion
});


export const unitConverterConfig: UnitConverterConfig = {
    length: {
        id: 'length',
        icon: StraightenIcon,
        nameKey: 'unit_length_name',
        descKey: 'unit_length_desc',
        explanation: { titleKey: 'unit_length_explanation_title', descKey: 'unit_length_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_length_faq1_q', a: 'unit_length_faq1_a' },
            { q: 'unit_length_faq2_q', a: 'unit_length_faq2_a' },
        ],
        units: [
            defineUnit('meter', 'unit_length_meter_name', 'unit_length_meter_symbol', 'unit_length_meter_desc', factor('1')),
            defineUnit('kilometer', 'unit_length_kilometer_name', 'unit_length_kilometer_symbol', 'unit_length_kilometer_desc', factor('1000')),
            defineUnit('centimeter', 'unit_length_centimeter_name', 'unit_length_centimeter_symbol', 'unit_length_centimeter_desc', factor('0.01')),
            defineUnit('millimeter', 'unit_length_millimeter_name', 'unit_length_millimeter_symbol', 'unit_length_millimeter_desc', factor('0.001')),
            defineUnit('micrometer', 'unit_length_micrometer_name', 'unit_length_micrometer_symbol', 'unit_length_micrometer_desc', factor('1e-6')),
            defineUnit('nanometer', 'unit_length_nanometer_name', 'unit_length_nanometer_symbol', 'unit_length_nanometer_desc', factor('1e-9')),
            defineUnit('angstrom', 'unit_length_angstrom_name', 'unit_length_angstrom_symbol', 'unit_length_angstrom_desc', factor('1e-10')),
            defineUnit('picometer', 'unit_length_picometer_name', 'unit_length_picometer_symbol', 'unit_length_picometer_desc', factor('1e-12')),
            defineUnit('femtometer', 'unit_length_femtometer_name', 'unit_length_femtometer_symbol', 'unit_length_femtometer_desc', factor('1e-15')),
            defineUnit('mile', 'unit_length_mile_name', 'unit_length_mile_symbol', 'unit_length_mile_desc', factor('1609.344')),
            defineUnit('yard', 'unit_length_yard_name', 'unit_length_yard_symbol', 'unit_length_yard_desc', factor('0.9144')),
            defineUnit('foot', 'unit_length_foot_name', 'unit_length_foot_symbol', 'unit_length_foot_desc', factor('0.3048')),
            defineUnit('inch', 'unit_length_inch_name', 'unit_length_inch_symbol', 'unit_length_inch_desc', factor('0.0254')),
            defineUnit('nautical-mile', 'unit_length_nautical_mile_name', 'unit_length_nautical_mile_symbol', 'unit_length_nautical_mile_desc', factor('1852')),
            defineUnit('light-year', 'unit_length_light_year_name', 'unit_length_light_year_symbol', 'unit_length_light_year_desc', factor('9460730472580800')),
            defineUnit('astronomical-unit', 'unit_length_astronomical_unit_name', 'unit_length_astronomical_unit_symbol', 'unit_length_astronomical_unit_desc', factor('149597870700')),
            defineUnit('parsec', 'unit_length_parsec_name', 'unit_length_parsec_symbol', 'unit_length_parsec_desc', factor('3.085677581491367e+16')),
        ],
    },
    mass: {
        id: 'mass',
        icon: MonitorWeightIcon,
        nameKey: 'unit_mass_name',
        descKey: 'unit_mass_desc',
        explanation: { titleKey: 'unit_mass_explanation_title', descKey: 'unit_mass_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_mass_faq1_q', a: 'unit_mass_faq1_a' },
            { q: 'unit_mass_faq2_q', a: 'unit_mass_faq2_a' },
        ],
        units: [
            defineUnit('kilogram', 'unit_mass_kilogram_name', 'unit_mass_kilogram_symbol', 'unit_mass_kilogram_desc', factor('1')),
            defineUnit('gram', 'unit_mass_gram_name', 'unit_mass_gram_symbol', 'unit_mass_gram_desc', factor('0.001')),
            defineUnit('milligram', 'unit_mass_milligram_name', 'unit_mass_milligram_symbol', 'unit_mass_milligram_desc', factor('1e-6')),
            defineUnit('microgram', 'unit_mass_microgram_name', 'unit_mass_microgram_symbol', 'unit_mass_microgram_desc', factor('1e-9')),
            defineUnit('nanogram', 'unit_mass_nanogram_name', 'unit_mass_nanogram_symbol', 'unit_mass_nanogram_desc', factor('1e-12')),
            defineUnit('picogram', 'unit_mass_picogram_name', 'unit_mass_picogram_symbol', 'unit_mass_picogram_desc', factor('1e-15')),
            defineUnit('metric-ton', 'unit_mass_metric_ton_name', 'unit_mass_metric_ton_symbol', 'unit_mass_metric_ton_desc', factor('1000')),
            defineUnit('pound', 'unit_mass_pound_name', 'unit_mass_pound_symbol', 'unit_mass_pound_desc', factor('0.45359237')),
            defineUnit('ounce', 'unit_mass_ounce_name', 'unit_mass_ounce_symbol', 'unit_mass_ounce_desc', factor('0.0283495231')),
            defineUnit('stone', 'unit_mass_stone_name', 'unit_mass_stone_symbol', 'unit_mass_stone_desc', factor('6.35029318')),
            defineUnit('ton-us', 'unit_mass_ton_us_name', 'unit_mass_ton_us_symbol', 'unit_mass_ton_us_desc', factor('907.18474')),
            defineUnit('ton-uk', 'unit_mass_ton_uk_name', 'unit_mass_ton_uk_symbol', 'unit_mass_ton_uk_desc', factor('1016.0469088')),
            defineUnit('atomic-mass-unit', 'unit_mass_atomic_mass_unit_name', 'unit_mass_atomic_mass_unit_symbol', 'unit_mass_atomic_mass_unit_desc', factor('1.66053906660e-27')),
            defineUnit('solar-mass', 'unit_mass_solar_mass_name', 'unit_mass_solar_mass_symbol', 'unit_mass_solar_mass_desc', factor('1.98847e+30')),
        ],
    },
    area: {
        id: 'area',
        icon: SquareFootIcon,
        nameKey: 'unit_area_name',
        descKey: 'unit_area_desc',
        explanation: { titleKey: 'unit_area_explanation_title', descKey: 'unit_area_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_area_faq1_q', a: 'unit_area_faq1_a' },
            { q: 'unit_area_faq2_q', a: 'unit_area_faq2_a' },
        ],
        units: [
            defineUnit('square-meter', 'unit_area_square_meter_name', 'unit_area_square_meter_symbol', 'unit_area_square_meter_desc', factor('1')),
            defineUnit('square-kilometer', 'unit_area_square_kilometer_name', 'unit_area_square_kilometer_symbol', 'unit_area_square_kilometer_desc', factor('1e6')),
            defineUnit('square-centimeter', 'unit_area_square_centimeter_name', 'unit_area_square_centimeter_symbol', 'unit_area_square_centimeter_desc', factor('1e-4')),
            defineUnit('square-millimeter', 'unit_area_square_millimeter_name', 'unit_area_square_millimeter_symbol', 'unit_area_square_millimeter_desc', factor('1e-6')),
            defineUnit('square-micrometer', 'unit_area_square_micrometer_name', 'unit_area_square_micrometer_symbol', 'unit_area_square_micrometer_desc', factor('1e-12')),
            defineUnit('square-nanometer', 'unit_area_square_nanometer_name', 'unit_area_square_nanometer_symbol', 'unit_area_square_nanometer_desc', factor('1e-18')),
            defineUnit('square-foot', 'unit_area_square_foot_name', 'unit_area_square_foot_symbol', 'unit_area_square_foot_desc', factor('0.09290304')),
            defineUnit('square-yard', 'unit_area_square_yard_name', 'unit_area_square_yard_symbol', 'unit_area_square_yard_desc', factor('0.83612736')),
            defineUnit('square-mile', 'unit_area_square_mile_name', 'unit_area_square_mile_symbol', 'unit_area_square_mile_desc', factor('2589988.110336')),
            defineUnit('acre', 'unit_area_acre_name', 'unit_area_acre_symbol', 'unit_area_acre_desc', factor('4046.8564224')),
            defineUnit('hectare', 'unit_area_hectare_name', 'unit_area_hectare_symbol', 'unit_area_hectare_desc', factor('1e4')),
        ],
    },
    volume: {
        id: 'volume',
        icon: ViewInArIcon,
        nameKey: 'unit_volume_name',
        descKey: 'unit_volume_desc',
        explanation: { titleKey: 'unit_volume_explanation_title', descKey: 'unit_volume_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_volume_faq1_q', a: 'unit_volume_faq1_a' },
            { q: 'unit_volume_faq2_q', a: 'unit_volume_faq2_a' },
        ],
        units: [
            defineUnit('cubic-meter', 'unit_volume_cubic_meter_name', 'unit_volume_cubic_meter_symbol', 'unit_volume_cubic_meter_desc', factor('1')),
            defineUnit('cubic-kilometer', 'unit_volume_cubic_kilometer_name', 'unit_volume_cubic_kilometer_symbol', 'unit_volume_cubic_kilometer_desc', factor('1e9')),
            defineUnit('cubic-centimeter', 'unit_volume_cubic_centimeter_name', 'unit_volume_cubic_centimeter_symbol', 'unit_volume_cubic_centimeter_desc', factor('1e-6')),
            defineUnit('cubic-millimeter', 'unit_volume_cubic_millimeter_name', 'unit_volume_cubic_millimeter_symbol', 'unit_volume_cubic_millimeter_desc', factor('1e-9')),
            defineUnit('liter', 'unit_volume_liter_name', 'unit_volume_liter_symbol', 'unit_volume_liter_desc', factor('0.001')),
            defineUnit('milliliter', 'unit_volume_milliliter_name', 'unit_volume_milliliter_symbol', 'unit_volume_milliliter_desc', factor('1e-6')),
            defineUnit('microliter', 'unit_volume_microliter_name', 'unit_volume_microliter_symbol', 'unit_volume_microliter_desc', factor('1e-9')),
            defineUnit('cubic-foot', 'unit_volume_cubic_foot_name', 'unit_volume_cubic_foot_symbol', 'unit_volume_cubic_foot_desc', factor('0.028316846592')),
            defineUnit('cubic-inch', 'unit_volume_cubic_inch_name', 'unit_volume_cubic_inch_symbol', 'unit_volume_cubic_inch_desc', factor('1.6387064e-5')),
            defineUnit('gallon-us', 'unit_volume_gallon_us_name', 'unit_volume_gallon_us_symbol', 'unit_volume_gallon_us_desc', factor('0.003785411784')),
            defineUnit('pint-us', 'unit_volume_pint_us_name', 'unit_volume_pint_us_symbol', 'unit_volume_pint_us_desc', factor('0.000473176473')),
            defineUnit('cubic-astronomical-unit', 'unit_volume_cubic_astronomical_unit_name', 'unit_volume_cubic_astronomical_unit_symbol', 'unit_volume_cubic_astronomical_unit_desc', factor('3.347928e+33')),
        ],
    },
    time: {
        id: 'time',
        icon: AccessTimeIcon,
        nameKey: 'unit_time_name',
        descKey: 'unit_time_desc',
        explanation: { titleKey: 'unit_time_explanation_title', descKey: 'unit_time_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_time_faq1_q', a: 'unit_time_faq1_a' },
            { q: 'unit_time_faq2_q', a: 'unit_time_faq2_a' },
        ],
        units: [
            defineUnit('second', 'unit_time_second_name', 'unit_time_second_symbol', 'unit_time_second_desc', factor('1')),
            defineUnit('minute', 'unit_time_minute_name', 'unit_time_minute_symbol', 'unit_time_minute_desc', factor('60')),
            defineUnit('hour', 'unit_time_hour_name', 'unit_time_hour_symbol', 'unit_time_hour_desc', factor('3600')),
            defineUnit('day', 'unit_time_day_name', 'unit_time_day_symbol', 'unit_time_day_desc', factor('86400')),
            defineUnit('year', 'unit_time_year_name', 'unit_time_year_symbol', 'unit_time_year_desc', factor('31557600')), // Based on Julian year (365.25 days)
            defineUnit('millisecond', 'unit_time_millisecond_name', 'unit_time_millisecond_symbol', 'unit_time_millisecond_desc', factor('0.001')),
            defineUnit('microsecond', 'unit_time_microsecond_name', 'unit_time_microsecond_symbol', 'unit_time_microsecond_desc', factor('1e-6')),
            defineUnit('nanosecond', 'unit_time_nanosecond_name', 'unit_time_nanosecond_symbol', 'unit_time_nanosecond_desc', factor('1e-9')),
            defineUnit('planck-time', 'unit_time_planck_time_name', 'unit_time_planck_time_symbol', 'unit_time_planck_time_desc', factor('5.391247e-44')),
        ],
    },
    speed: {
        id: 'speed',
        icon: ShutterSpeedIcon,
        nameKey: 'unit_speed_name',
        descKey: 'unit_speed_desc',
        explanation: { titleKey: 'unit_speed_explanation_title', descKey: 'unit_speed_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_speed_faq1_q', a: 'unit_speed_faq1_a' },
            { q: 'unit_speed_faq2_q', a: 'unit_speed_faq2_a' },
        ],
        units: [
            defineUnit('meter-per-second', 'unit_speed_meter_per_second_name', 'unit_speed_meter_per_second_symbol', 'unit_speed_meter_per_second_desc', factor('1')),
            defineUnit('kilometer-per-hour', 'unit_speed_kilometer_per_hour_name', 'unit_speed_kilometer_per_hour_symbol', 'unit_speed_kilometer_per_hour_desc', factor('0.2777777777777778')), // 1000/3600
            defineUnit('mile-per-hour', 'unit_speed_mile_per_hour_name', 'unit_speed_mile_per_hour_symbol', 'unit_speed_mile_per_hour_desc', factor('0.44704')), // 1609.344/3600
            defineUnit('knot', 'unit_speed_knot_name', 'unit_speed_knot_symbol', 'unit_speed_knot_desc', factor('0.5144444444444445')), // 1852/3600
            defineUnit('light-speed', 'unit_speed_light_speed_name', 'unit_speed_light_speed_symbol', 'unit_speed_light_speed_desc', factor('299792458')),
            defineUnit('mach', 'unit_speed_mach_name', 'unit_speed_mach_symbol', 'unit_speed_mach_desc', factor('343')), // Mach 1 at sea level, standard conditions
        ],
    },
    acceleration: {
        id: 'acceleration',
        icon: SpeedIcon,
        nameKey: 'unit_acceleration_name',
        descKey: 'unit_acceleration_desc',
        explanation: { titleKey: 'unit_acceleration_explanation_title', descKey: 'unit_acceleration_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_acceleration_faq1_q', a: 'unit_acceleration_faq1_a' },
            { q: 'unit_acceleration_faq2_q', a: 'unit_acceleration_faq2_a' },
        ],
        units: [
            defineUnit('meter-per-second-squared', 'unit_acceleration_meter_per_second_squared_name', 'unit_acceleration_meter_per_second_squared_symbol', 'unit_acceleration_meter_per_second_squared_desc', factor('1')),
            defineUnit('gal', 'unit_acceleration_gal_name', 'unit_acceleration_gal_symbol', 'unit_acceleration_gal_desc', factor('0.01')),
            defineUnit('standard-gravity', 'unit_acceleration_standard_gravity_name', 'unit_acceleration_standard_gravity_symbol', 'unit_acceleration_standard_gravity_desc', factor('9.80665')),
        ],
    },
    force: {
        id: 'force',
        icon: FitnessCenterIcon,
        nameKey: 'unit_force_name',
        descKey: 'unit_force_desc',
        explanation: { titleKey: 'unit_force_explanation_title', descKey: 'unit_force_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_force_faq1_q', a: 'unit_force_faq1_a' },
            { q: 'unit_force_faq2_q', a: 'unit_force_faq2_a' },
        ],
        units: [
            defineUnit('newton', 'unit_force_newton_name', 'unit_force_newton_symbol', 'unit_force_newton_desc', factor('1')),
            defineUnit('kilonewton', 'unit_force_kilonewton_name', 'unit_force_kilonewton_symbol', 'unit_force_kilonewton_desc', factor('1000')),
            defineUnit('pound-force', 'unit_force_pound_force_name', 'unit_force_pound_force_symbol', 'unit_force_pound_force_desc', factor('4.4482216152605')),
            defineUnit('dyne', 'unit_force_dyne_name', 'unit_force_dyne_symbol', 'unit_force_dyne_desc', factor('0.00001')),
            defineUnit('kilogram-force', 'unit_force_kilogram_force_name', 'unit_force_kilogram_force_symbol', 'unit_force_kilogram_force_desc', factor('9.80665')),
        ],
    },
    pressure: {
        id: 'pressure',
        icon: WavesIcon,
        nameKey: 'unit_pressure_name',
        descKey: 'unit_pressure_desc',
        explanation: { titleKey: 'unit_pressure_explanation_title', descKey: 'unit_pressure_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_pressure_faq1_q', a: 'unit_pressure_faq1_a' },
            { q: 'unit_pressure_faq2_q', a: 'unit_pressure_faq2_a' },
        ],
        units: [
            defineUnit('pascal', 'unit_pressure_pascal_name', 'unit_pressure_pascal_symbol', 'unit_pressure_pascal_desc', factor('1')),
            defineUnit('kilopascal', 'unit_pressure_kilopascal_name', 'unit_pressure_kilopascal_symbol', 'unit_pressure_kilopascal_desc', factor('1000')),
            defineUnit('bar', 'unit_pressure_bar_name', 'unit_pressure_bar_symbol', 'unit_pressure_bar_desc', factor('100000')),
            defineUnit('atmosphere', 'unit_pressure_atmosphere_name', 'unit_pressure_atmosphere_symbol', 'unit_pressure_atmosphere_desc', factor('101325')),
            defineUnit('millimeter-mercury', 'unit_pressure_millimeter_mercury_name', 'unit_pressure_millimeter_mercury_symbol', 'unit_pressure_millimeter_mercury_desc', factor('133.322387415')),
            defineUnit('torr', 'unit_pressure_torr_name', 'unit_pressure_torr_symbol', 'unit_pressure_torr_desc', factor('133.32236842105263')),
            defineUnit('pound-per-square-inch', 'unit_pressure_pound_per_square_inch_name', 'unit_pressure_pound_per_square_inch_symbol', 'unit_pressure_pound_per_square_inch_desc', factor('6894.757293168361')),
        ],
    },
    energy: {
        id: 'energy',
        icon: FlashOnIcon,
        nameKey: 'unit_energy_name',
        descKey: 'unit_energy_desc',
        explanation: { titleKey: 'unit_energy_explanation_title', descKey: 'unit_energy_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_energy_faq1_q', a: 'unit_energy_faq1_a' },
            { q: 'unit_energy_faq2_q', a: 'unit_energy_faq2_a' },
        ],
        units: [
            defineUnit('joule', 'unit_energy_joule_name', 'unit_energy_joule_symbol', 'unit_energy_joule_desc', factor('1')),
            defineUnit('kilojoule', 'unit_energy_kilojoule_name', 'unit_energy_kilojoule_symbol', 'unit_energy_kilojoule_desc', factor('1000')),
            defineUnit('calorie', 'unit_energy_calorie_name', 'unit_energy_calorie_symbol', 'unit_energy_calorie_desc', factor('4.184')),
            defineUnit('kilowatt-hour', 'unit_energy_kilowatt_hour_name', 'unit_energy_kilowatt_hour_symbol', 'unit_energy_kilowatt_hour_desc', factor('3600000')),
            defineUnit('electron-volt', 'unit_energy_electron_volt_name', 'unit_energy_electron_volt_symbol', 'unit_energy_electron_volt_desc', factor('1.602176634e-19')),
            defineUnit('erg', 'unit_energy_erg_name', 'unit_energy_erg_symbol', 'unit_energy_erg_desc', factor('1e-7')),
            defineUnit('planck-energy', 'unit_energy_planck_energy_name', 'unit_energy_planck_energy_symbol', 'unit_energy_planck_energy_desc', factor('1.9561e9')),
        ],
    },
    power: {
        id: 'power',
        icon: PowerIcon,
        nameKey: 'unit_power_name',
        descKey: 'unit_power_desc',
        explanation: { titleKey: 'unit_power_explanation_title', descKey: 'unit_power_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_power_faq1_q', a: 'unit_power_faq1_a' },
            { q: 'unit_power_faq2_q', a: 'unit_power_faq2_a' },
        ],
        units: [
            defineUnit('watt', 'unit_power_watt_name', 'unit_power_watt_symbol', 'unit_power_watt_desc', factor('1')),
            defineUnit('kilowatt', 'unit_power_kilowatt_name', 'unit_power_kilowatt_symbol', 'unit_power_kilowatt_desc', factor('1000')),
            defineUnit('horsepower', 'unit_power_horsepower_name', 'unit_power_horsepower_symbol', 'unit_power_horsepower_desc', factor('745.69987158227022')),
            defineUnit('milliwatt', 'unit_power_milliwatt_name', 'unit_power_milliwatt_symbol', 'unit_power_milliwatt_desc', factor('0.001')),
            defineUnit('gigawatt', 'unit_power_gigawatt_name', 'unit_power_gigawatt_symbol', 'unit_power_gigawatt_desc', factor('1e9')),
        ],
    },
    temperature: {
        id: 'temperature',
        icon: DeviceThermostatIcon,
        nameKey: 'unit_temperature_name',
        descKey: 'unit_temperature_desc',
        explanation: { titleKey: 'unit_temperature_explanation_title', descKey: 'unit_temperature_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_temperature_faq1_q', a: 'unit_temperature_faq1_a' },
            { q: 'unit_temperature_faq2_q', a: 'unit_temperature_faq2_a' },
        ],
        units: [
            defineUnit('celsius', 'unit_temperature_celsius_name', 'unit_temperature_celsius_symbol', 'unit_temperature_celsius_desc', factor('1')),
            defineUnit('fahrenheit', 'unit_temperature_fahrenheit_name', 'unit_temperature_fahrenheit_symbol', 'unit_temperature_fahrenheit_desc', affine('0.5555555555555556', '32')), // (F-32)*(5/9) to C, C*(9/5)+32 to F
            defineUnit('kelvin', 'unit_temperature_kelvin_name', 'unit_temperature_kelvin_symbol', 'unit_temperature_kelvin_desc', affine('1', '273.15')), // K-273.15 to C, C+273.15 to K
            defineUnit('rankine', 'unit_temperature_rankine_name', 'unit_temperature_rankine_symbol', 'unit_temperature_rankine_desc', affine('0.5555555555555556', '459.67')), // (R-459.67)*(5/9) to C, C*(9/5)+459.67 to R
            defineUnit('delisle', 'unit_temperature_delisle_name', 'unit_temperature_delisle_symbol', 'unit_temperature_delisle_desc', {
                toBase: (v: Big) => new Big(100).minus(v).times('0.6666666666666666'), // (100-De)*(2/3) to C
                fromBase: (v: Big) => new Big(100).minus(v.times('1.5')), // 100-C*(3/2) to De
            }),
            defineUnit('newton', 'unit_temperature_newton_name', 'unit_temperature_newton_symbol', 'unit_temperature_newton_desc', factor('3.0303030303030303')), // C*(33/100) to N, N*(100/33) to C
            defineUnit('reaumur', 'unit_temperature_reaumur_name', 'unit_temperature_reaumur_symbol', 'unit_temperature_reaumur_desc', factor('1.25')), // C*(4/5) to Ré, Ré*(5/4) to C
            defineUnit('romer', 'unit_temperature_romer_name', 'unit_temperature_romer_symbol', 'unit_temperature_romer_desc', {
                toBase: (v: Big) => v.minus('7.5').times('1.9047619047619047'), // (Rø-7.5)*(40/21) to C
                fromBase: (v: Big) => v.times('0.525').plus('7.5'), // C*(21/40)+7.5 to Rø
            }),
            defineUnit('planck-temperature', 'unit_temperature_planck_temperature_name', 'unit_temperature_planck_temperature_symbol', 'unit_temperature_planck_temperature_desc', factor('1.416808e32')), // Conversion relative to Kelvin, then to Celsius
        ],
    },
    angle: {
        id: 'angle',
        icon: AngleIcon,
        nameKey: 'unit_angle_name',
        descKey: 'unit_angle_desc',
        explanation: { titleKey: 'unit_angle_explanation_title', descKey: 'unit_angle_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_angle_faq1_q', a: 'unit_angle_faq1_a' },
            { q: 'unit_angle_faq2_q', a: 'unit_angle_faq2_a' },
        ],
        units: [
            defineUnit('degree', 'unit_angle_degree_name', 'unit_angle_degree_symbol', 'unit_angle_degree_desc', factor('1')),
            defineUnit('radian', 'unit_angle_radian_name', 'unit_angle_radian_symbol', 'unit_angle_radian_desc', factor('57.29577951308232')), // 180/π
            defineUnit('gradian', 'unit_angle_gradian_name', 'unit_angle_gradian_symbol', 'unit_angle_gradian_desc', factor('0.9')), // 360/400
            defineUnit('arcminute', 'unit_angle_arcminute_name', 'unit_angle_arcminute_symbol', 'unit_angle_arcminute_desc', factor('0.016666666666666666')), // 1/60
            defineUnit('arcsecond', 'unit_angle_arcsecond_name', 'unit_angle_arcsecond_symbol', 'unit_angle_arcsecond_desc', factor('0.0002777777777777778')), // 1/3600
            defineUnit('turn', 'unit_angle_turn_name', 'unit_angle_turn_symbol', 'unit_angle_turn_desc', factor('360')), // 1 turn = 360°
            defineUnit('milliradian', 'unit_angle_milliradian_name', 'unit_angle_milliradian_symbol', 'unit_angle_milliradian_desc', factor('0.05729577951308232')), // rad/1000
            defineUnit('degree-of-arc', 'unit_angle_degree_of_arc_name', 'unit_angle_degree_of_arc_symbol', 'unit_angle_degree_of_arc_desc', factor('1')), // Synonymous with degree
        ],
    },
    frequency: {
        id: 'frequency',
        icon: VibrationIcon,
        nameKey: 'unit_frequency_name',
        descKey: 'unit_frequency_desc',
        explanation: { titleKey: 'unit_frequency_explanation_title', descKey: 'unit_frequency_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_frequency_faq1_q', a: 'unit_frequency_faq1_a' },
            { q: 'unit_frequency_faq2_q', a: 'unit_frequency_faq2_a' },
        ],
        units: [
            defineUnit('hertz', 'unit_frequency_hertz_name', 'unit_frequency_hertz_symbol', 'unit_frequency_hertz_desc', factor('1')),
            defineUnit('kilohertz', 'unit_frequency_kilohertz_name', 'unit_frequency_kilohertz_symbol', 'unit_frequency_kilohertz_desc', factor('1000')),
            defineUnit('megahertz', 'unit_frequency_megahertz_name', 'unit_frequency_megahertz_symbol', 'unit_frequency_megahertz_desc', factor('1000000')),
            defineUnit('gigahertz', 'unit_frequency_gigahertz_name', 'unit_frequency_gigahertz_symbol', 'unit_frequency_gigahertz_desc', factor('1000000000')),
            defineUnit('terahertz', 'unit_frequency_terahertz_name', 'unit_frequency_terahertz_symbol', 'unit_frequency_terahertz_desc', factor('1000000000000')),
            defineUnit('millihertz', 'unit_frequency_millihertz_name', 'unit_frequency_millihertz_symbol', 'unit_frequency_millihertz_desc', factor('0.001')),
            defineUnit('microhertz', 'unit_frequency_microhertz_name', 'unit_frequency_microhertz_symbol', 'unit_frequency_microhertz_desc', factor('0.000001')),
            defineUnit('nanohertz', 'unit_frequency_nanohertz_name', 'unit_frequency_nanohertz_symbol', 'unit_frequency_nanohertz_desc', factor('0.000000001')),
            defineUnit('cycles-per-second', 'unit_frequency_cycles_per_second_name', 'unit_frequency_cycles_per_second_symbol', 'unit_frequency_cycles_per_second_desc', factor('1')),
        ],
    },
    electric_charge: {
        id: 'electric_charge',
        icon: BatteryChargingFullIcon,
        nameKey: 'unit_electric_charge_name',
        descKey: 'unit_electric_charge_desc',
        explanation: { titleKey: 'unit_electric_charge_explanation_title', descKey: 'unit_electric_charge_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_electric_charge_faq1_q', a: 'unit_electric_charge_faq1_a' },
            { q: 'unit_electric_charge_faq2_q', a: 'unit_electric_charge_faq2_a' },
        ],
        units: [
            defineUnit('coulomb', 'unit_electric_charge_coulomb_name', 'unit_electric_charge_coulomb_symbol', 'unit_electric_charge_coulomb_desc', factor('1')),
            defineUnit('faraday', 'unit_electric_charge_faraday_name', 'unit_electric_charge_faraday_symbol', 'unit_electric_charge_faraday_desc', factor('96485.33212')), // Faraday constant
            defineUnit('elementary-charge', 'unit_electric_charge_elementary_charge_name', 'unit_electric_charge_elementary_charge_symbol', 'unit_electric_charge_elementary_charge_desc', factor('1.602176634e-19')), // Elementary charge
            defineUnit('ampere-hour', 'unit_electric_charge_ampere_hour_name', 'unit_electric_charge_ampere_hour_symbol', 'unit_electric_charge_ampere_hour_desc', factor('3600')), // 1 Ah = 3600 C
            defineUnit('milliampere-hour', 'unit_electric_charge_milliampere_hour_name', 'unit_electric_charge_milliampere_hour_symbol', 'unit_electric_charge_milliampere_hour_desc', factor('3.6')), // 1 mAh = 3.6 C
            defineUnit('statcoulomb', 'unit_electric_charge_statcoulomb_name', 'unit_electric_charge_statcoulomb_symbol', 'unit_electric_charge_statcoulomb_desc', factor('3.335641e-10')), // 1 statC ≈ 3.335641e-10 C
        ],
    },
    electric_current: {
        id: 'electric_current',
        icon: FlashAutoIcon,
        nameKey: 'unit_electric_current_name',
        descKey: 'unit_electric_current_desc',
        explanation: { titleKey: 'unit_electric_current_explanation_title', descKey: 'unit_electric_current_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_electric_current_faq1_q', a: 'unit_electric_current_faq1_a' },
            { q: 'unit_electric_current_faq2_q', a: 'unit_electric_current_faq2_a' },
        ],
        units: [
            defineUnit('ampere', 'unit_electric_current_ampere_name', 'unit_electric_current_ampere_symbol', 'unit_electric_current_ampere_desc', factor('1')),
            defineUnit('milliampere', 'unit_electric_current_milliampere_name', 'unit_electric_current_milliampere_symbol', 'unit_electric_current_milliampere_desc', factor('0.001')),
            defineUnit('microampere', 'unit_electric_current_microampere_name', 'unit_electric_current_microampere_symbol', 'unit_electric_current_microampere_desc', factor('0.000001')),
            defineUnit('kiloampere', 'unit_electric_current_kiloampere_name', 'unit_electric_current_kiloampere_symbol', 'unit_electric_current_kiloampere_desc', factor('1000')),
            defineUnit('abampere', 'unit_electric_current_abampere_name', 'unit_electric_current_abampere_symbol', 'unit_electric_current_abampere_desc', factor('10')), // 1 abA = 10 A
        ],
    },
    voltage: {
        id: 'voltage',
        icon: BoltIcon,
        nameKey: 'unit_voltage_name',
        descKey: 'unit_voltage_desc',
        explanation: { titleKey: 'unit_voltage_explanation_title', descKey: 'unit_voltage_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_voltage_faq1_q', a: 'unit_voltage_faq1_a' },
            { q: 'unit_voltage_faq2_q', a: 'unit_voltage_faq2_a' },
        ],
        units: [
            defineUnit('volt', 'unit_voltage_volt_name', 'unit_voltage_volt_symbol', 'unit_voltage_volt_desc', factor('1')),
            defineUnit('millivolt', 'unit_voltage_millivolt_name', 'unit_voltage_millivolt_symbol', 'unit_voltage_millivolt_desc', factor('0.001')),
            defineUnit('kilovolt', 'unit_voltage_kilovolt_name', 'unit_voltage_kilovolt_symbol', 'unit_voltage_kilovolt_desc', factor('1000')),
            defineUnit('microvolt', 'unit_voltage_microvolt_name', 'unit_voltage_microvolt_symbol', 'unit_voltage_microvolt_desc', factor('0.000001')),
            defineUnit('megavolt', 'unit_voltage_megavolt_name', 'unit_voltage_megavolt_symbol', 'unit_voltage_megavolt_desc', factor('1000000')),
            defineUnit('statvolt', 'unit_voltage_statvolt_name', 'unit_voltage_statvolt_symbol', 'unit_voltage_statvolt_desc', factor('299.792458')), // 1 statV ≈ 299.792458 V
        ],
    },
    resistance: {
        id: 'resistance',
        icon: OhmIcon,
        nameKey: 'unit_resistance_name',
        descKey: 'unit_resistance_desc',
        explanation: { titleKey: 'unit_resistance_explanation_title', descKey: 'unit_resistance_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_resistance_faq1_q', a: 'unit_resistance_faq1_a' },
            { q: 'unit_resistance_faq2_q', a: 'unit_resistance_faq2_a' },
        ],
        units: [
            defineUnit('ohm', 'unit_resistance_ohm_name', 'unit_resistance_ohm_symbol', 'unit_resistance_ohm_desc', factor('1')),
            defineUnit('kiloohm', 'unit_resistance_kiloohm_name', 'unit_resistance_kiloohm_symbol', 'unit_resistance_kiloohm_desc', factor('1000')),
            defineUnit('megaohm', 'unit_resistance_megaohm_name', 'unit_resistance_megaohm_symbol', 'unit_resistance_megaohm_desc', factor('1000000')),
            defineUnit('microohm', 'unit_resistance_microohm_name', 'unit_resistance_microohm_symbol', 'unit_resistance_microohm_desc', factor('0.000001')),
            defineUnit('abohm', 'unit_resistance_abohm_name', 'unit_resistance_abohm_symbol', 'unit_resistance_abohm_desc', factor('1e-9')), // 1 abΩ = 1e-9 Ω
        ],
    },
    conductance: {
        id: 'conductance',
        icon: ConductivityIcon,
        nameKey: 'unit_conductance_name',
        descKey: 'unit_conductance_desc',
        explanation: { titleKey: 'unit_conductance_explanation_title', descKey: 'unit_conductance_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_conductance_faq1_q', a: 'unit_conductance_faq1_a' },
            { q: 'unit_conductance_faq2_q', a: 'unit_conductance_faq2_a' },
        ],
        units: [
            defineUnit('siemens', 'unit_conductance_siemens_name', 'unit_conductance_siemens_symbol', 'unit_conductance_siemens_desc', factor('1')),
            defineUnit('millisiemens', 'unit_conductance_millisiemens_name', 'unit_conductance_millisiemens_symbol', 'unit_conductance_millisiemens_desc', factor('0.001')),
            defineUnit('microsiemens', 'unit_conductance_microsiemens_name', 'unit_conductance_microsiemens_symbol', 'unit_conductance_microsiemens_desc', factor('0.000001')),
            defineUnit('mho', 'unit_conductance_mho_name', 'unit_conductance_mho_symbol', 'unit_conductance_mho_desc', factor('1')), // Mho is equivalent to Siemens
        ],
    },
    magnetic_flux: {
        id: 'magnetic_flux',
        icon: MagneticIcon,
        nameKey: 'unit_magnetic_flux_name',
        descKey: 'unit_magnetic_flux_desc',
        explanation: { titleKey: 'unit_magnetic_flux_explanation_title', descKey: 'unit_magnetic_flux_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_magnetic_flux_faq1_q', a: 'unit_magnetic_flux_faq1_a' },
            { q: 'unit_magnetic_flux_faq2_q', a: 'unit_magnetic_flux_faq2_a' },
        ],
        units: [
            defineUnit('weber', 'unit_magnetic_flux_weber_name', 'unit_magnetic_flux_weber_symbol', 'unit_magnetic_flux_weber_desc', factor('1')),
            defineUnit('maxwell', 'unit_magnetic_flux_maxwell_name', 'unit_magnetic_flux_maxwell_symbol', 'unit_magnetic_flux_maxwell_desc', factor('1e-8')), // 1 Mx = 1e-8 Wb
        ],
    },
    magnetic_field_strength: {
        id: 'magnetic_field_strength',
        icon: MagneticIcon,
        nameKey: 'unit_magnetic_field_strength_name',
        descKey: 'unit_magnetic_field_strength_desc',
        explanation: { titleKey: 'unit_magnetic_field_strength_explanation_title', descKey: 'unit_magnetic_field_strength_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_magnetic_field_strength_faq1_q', a: 'unit_magnetic_field_strength_faq1_a' },
            { q: 'unit_magnetic_field_strength_faq2_q', a: 'unit_magnetic_field_strength_faq2_a' },
        ],
        units: [
            defineUnit('tesla', 'unit_magnetic_field_strength_tesla_name', 'unit_magnetic_field_strength_tesla_symbol', 'unit_magnetic_field_strength_tesla_desc', factor('1')),
            defineUnit('gauss', 'unit_magnetic_field_strength_gauss_name', 'unit_magnetic_field_strength_gauss_symbol', 'unit_magnetic_field_strength_gauss_desc', factor('1e-4')), // 1 G = 1e-4 T
            defineUnit('nanotesla', 'unit_magnetic_field_strength_nanotesla_name', 'unit_magnetic_field_strength_nanotesla_symbol', 'unit_magnetic_field_strength_nanotesla_desc', factor('1e-9')), // 1 nT = 1e-9 T
        ],
    },
    density: {
        id: 'density',
        icon: DensityIcon,
        nameKey: 'unit_density_name',
        descKey: 'unit_density_desc',
        explanation: { titleKey: 'unit_density_explanation_title', descKey: 'unit_density_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_density_faq1_q', a: 'unit_density_faq1_a' },
            { q: 'unit_density_faq2_q', a: 'unit_density_faq2_a' },
        ],
        units: [
            defineUnit('kilogram-per-cubic-meter', 'unit_density_kilogram_per_cubic_meter_name', 'unit_density_kilogram_per_cubic_meter_symbol', 'unit_density_kilogram_per_cubic_meter_desc', factor('1')),
            defineUnit('gram-per-cubic-centimeter', 'unit_density_gram_per_cubic_centimeter_name', 'unit_density_gram_per_cubic_centimeter_symbol', 'unit_density_gram_per_cubic_centimeter_desc', factor('1000')), // 1 g/cm³ = 1000 kg/m³
            defineUnit('kilogram-per-liter', 'unit_density_kilogram_per_liter_name', 'unit_density_kilogram_per_liter_symbol', 'unit_density_kilogram_per_liter_desc', factor('1000')), // 1 kg/L = 1000 kg/m³
        ],
    },
    concentration: {
        id: 'concentration',
        icon: ConcentrationIcon,
        nameKey: 'unit_concentration_name',
        descKey: 'unit_concentration_desc',
        explanation: { titleKey: 'unit_concentration_explanation_title', descKey: 'unit_concentration_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_concentration_faq1_q', a: 'unit_concentration_faq1_a' },
            { q: 'unit_concentration_faq2_q', a: 'unit_concentration_faq2_a' },
        ],
        units: [
            defineUnit('mole-per-liter', 'unit_concentration_mole_per_liter_name', 'unit_concentration_mole_per_liter_symbol', 'unit_concentration_mole_per_liter_desc', factor('1')),
            defineUnit('gram-per-liter', 'unit_concentration_gram_per_liter_name', 'unit_concentration_gram_per_liter_symbol', 'unit_concentration_gram_per_liter_desc', factor('1')), // Requires molar mass for conversion; assuming 1 for simplicity
            defineUnit('mole-per-cubic-meter', 'unit_concentration_mole_per_cubic_meter_name', 'unit_concentration_mole_per_cubic_meter_symbol', 'unit_concentration_mole_per_cubic_meter_desc', factor('0.001')), // 1 mol/m³ = 0.001 mol/L
            defineUnit('ppm', 'unit_concentration_ppm_name', 'unit_concentration_ppm_symbol', 'unit_concentration_ppm_desc', factor('1e-6')), // 1 ppm = 1e-6 g/L (mass-based, assuming water density)
            defineUnit('ppb', 'unit_concentration_ppb_name', 'unit_concentration_ppb_symbol', 'unit_concentration_ppb_desc', factor('1e-9')), // 1 ppb = 1e-9 g/L
        ],
    },
    radiation_dose: {
        id: 'radiation_dose',
        icon: RadiationIcon,
        nameKey: 'unit_radiation_dose_name',
        descKey: 'unit_radiation_dose_desc',
        explanation: { titleKey: 'unit_radiation_dose_explanation_title', descKey: 'unit_radiation_dose_explanation_desc' },
        whyUs: { titleKey: 'unit_common_why_us_title', descKey: 'unit_common_why_us_desc' },
        faqs: [
            { q: 'unit_radiation_dose_faq1_q', a: 'unit_radiation_dose_faq1_a' },
            { q: 'unit_radiation_dose_faq2_q', a: 'unit_radiation_dose_faq2_a' },
        ],
        units: [
            defineUnit('sievert', 'unit_radiation_dose_sievert_name', 'unit_radiation_dose_sievert_symbol', 'unit_radiation_dose_sievert_desc', factor('1')),
            defineUnit('rem', 'unit_radiation_dose_rem_name', 'unit_radiation_dose_rem_symbol', 'unit_radiation_dose_rem_desc', factor('0.01')), // 1 rem = 0.01 Sv
            defineUnit('gray', 'unit_radiation_dose_gray_name', 'unit_radiation_dose_gray_symbol', 'unit_radiation_dose_gray_desc', factor('1')), // Gray and Sievert are equivalent for certain radiation types
            defineUnit('rad', 'unit_radiation_dose_rad_name', 'unit_radiation_dose_rad_symbol', 'unit_radiation_dose_rad_desc', factor('0.01')), // 1 rad = 0.01 Gy
        ],
    },
};